import { lazy, Suspense } from "react";
import { Route, Routes, useLocation, Navigate } from "react-router-dom";
import { ROUTES } from "./constants/routeList";
import CallingSharePreSchedule from "../_containers/Dashboard/Ticket/Devolution/SharePreschedule/SharePreschedule";

// Private
const Dashboard = ROUTES.PRIVATE.loadDashboard;
const DashboardEquipment = ROUTES.PRIVATE.loadDashboardEquipment;
const FinanceHome = ROUTES.PRIVATE.loadFinanceHome;
const FinanceDuplicate = ROUTES.PRIVATE.loadFinanceDuplicate;
const OpenNewContest = ROUTES.PRIVATE.loadOpenNewContest;
const StepperPage = ROUTES.PRIVATE.loadStepperPage;
const DashboardFavoriteCardHome = ROUTES.PRIVATE.loadDashboardFavoriteCardHome;
const TicketHome = ROUTES.PRIVATE.loadTicketHome;
const CallingDeiveryCopy = ROUTES.PRIVATE.loadCallingDeiveryCopy;
const PrescheduleDevolutionContainer = ROUTES.PRIVATE.loadPrescheduleDevolutionContainer;
const CallingSchedule = ROUTES.PRIVATE.loadCallingSchedule;
const TrackPage = ROUTES.PRIVATE.loadTrackPage;
const ContestTrackPage = ROUTES.PRIVATE.loadContestTrackPage;
const TechnicalTicketStepsContainer = ROUTES.PRIVATE.loadTechnicalTicketStepsContainer;
const TechnicalTicketOpenNewTicket = ROUTES.PRIVATE.loadTechnicalTicketOpenNewTicket;
const NotFound = ROUTES.PRIVATE.loadNotFound;

// Public
const SignIn = ROUTES.PUBLIC.loadSignIn;
const HomeCreateAccount = ROUTES.PUBLIC.loadHomeCreateAccount;
const Login = ROUTES.PUBLIC.loadLogin;
const PasswordRecover = ROUTES.PUBLIC.loadPasswordRecover;
const RecoverSuccess = ROUTES.PUBLIC.loadRecoverSuccess;
const Register = ROUTES.PUBLIC.loadRegister;
const Reset = ROUTES.PUBLIC.loadReset;
const EnterResetStep = ROUTES.PUBLIC.loadEnterResetStep;
const ResetSuccess = ROUTES.PUBLIC.loadReset;

// QR Portal - Componentes Públicos (Unificados)
const QRPortalHome = lazy(() => import("../_containers/QRPortal/QRPortalHome/QRPortalHome"));
const QRCentralRecursos = lazy(() => import("../_containers/QRPortal/QRCentralRecursos/QRCentralRecursos"));
const QRManualViewer = lazy(() => import("../_containers/QRPortal/QRManualViewer/QRManualViewer"));
const QRVideosInstitucionais = lazy(() => import("../_containers/QRPortal/QRVideosInstitucionais/QRVideosInstitucionais"));

// Portal Técnico - Componentes de Autenticação
const PortalTecnicoLogin = lazy(() => import("../_containers/QRPortal/PortalTecnico/Login/PortalTecnicoLogin"));
const AzureCallback = lazy(() => import("../_containers/QRPortal/PortalTecnico/Callback/AzureCallback"));

// Home do Portal Técnico
const PortalTecnicoHome = lazy(() => import("../_containers/QRPortal/PortalTecnico/PortalTecnicoHome/PortalTecnicoHome"));

// Checklists Técnicos
const ChecklistInspecao = lazy(() => import("../_containers/QRPortal/PortalTecnico/ChecklistInspecao"));
const ChecklistPecas = lazy(() => import("../_containers/QRPortal/PortalTecnico/ChecklistPecas"));

// Checklist PEMT - Portal Cliente
const ChecklistPEMT = lazy(() => import("../_containers/QRPortal/QRChecklistPEMT"));

// Viewers Especializados (se quiser usar no futuro)
// const PortalClienteManualViewer = lazy(() => import("../_containers/QRPortal/PortalClienteManualViewer/PortalClienteManualViewer"));
// const PortalTecnicoManualViewer = lazy(() => import("../_containers/QRPortal/PortalTecnicoManualViewer/PortalTecnicoManualViewer"));

const SuspensePage = lazy(() => import("@layout/Suspense/SuspensePage"));

/** Redireciona rotas antigas /portal-qr/conteudo/:frota para /portal-qr/familia/:familia */
const RedirectFrotaToFamilia = () => {
  const location = useLocation();
  const path = location.pathname;
  const frotaMatch = path.match(/\/portal-qr\/conteudo\/(.+)/);
  if (frotaMatch && frotaMatch[1]) {
    return <Navigate to={`/portal-qr/familia/${frotaMatch[1]}`} replace />;
  }
  return <NotFound />;
};

/** Redireciona rotas antigas de manual para a rota nova */
const RedirectOldManualRoutes = () => {
  const location = useLocation();
  const path = location.pathname;

  // /portal-qr/manual/:familia  ->  /portal-qr/familia/:familia/manual-viewer
  const manualMatch = path.match(/\/portal-qr\/manual\/(.+)/);
  if (manualMatch && manualMatch[1]) {
    const familia = manualMatch[1];
    const searchParams = new URLSearchParams(location.search);
    const modelo = searchParams.get("modelo");
    const frota = searchParams.get("frota");
    
    let newPath = `/portal-qr/familia/${familia}/manual-viewer`;
    const params = [];
    if (modelo) params.push(`modelo=${encodeURIComponent(modelo)}`);
    if (frota) params.push(`frota=${encodeURIComponent(frota)}`);
    if (params.length > 0) newPath += `?${params.join('&')}`;
    
    return <Navigate to={newPath} replace />;
  }

  return <NotFound />;
};

/** Proteção do Dashboard principal */
const ProtectedRouteDashboard = ({ children }) => {
  const connected = localStorage.getItem("access_token");
  if (!connected) return <Navigate to="/" />;
  return children;
};

/** Proteção do Portal Técnico */
const ProtectedPortalTecnico = ({ children }) => {
  const tecnicoToken = sessionStorage.getItem("portal_tecnico_token");
  if (!tecnicoToken) return <Navigate to="/portal-tecnico/login" />;
  return children;
};

/** Proteção da Home - permite QR Portal público e Portal Técnico com suas próprias proteções */
const ProtectedHome = ({ children }) => {
  const connected = localStorage.getItem("access_token");
  const currentPath = window.location.pathname;

  // Portal QR é público (não exige login)
  if (currentPath.startsWith("/portal-qr")) return children;
  
  // Portal Técnico tem sua própria proteção via ProtectedPortalTecnico
  if (currentPath.startsWith("/portal-tecnico")) return children;

  // Se está logado e não é portal QR/Técnico, vai pro dashboard
  if (!!connected) return <Navigate to="/Painel-de-controle" />;

  return children;
};

function AnimatedRoutes() {
  const path = useLocation();
  const dashboardPath = "/Painel-de-controle";

  return (
    <Suspense fallback={<SuspensePage />}>
      <Routes location={path}>
        {/* === PÚBLICO / AUTENTICAÇÃO === */}
        <Route
          path="/"
          element={
            <ProtectedHome>
              <Login />
            </ProtectedHome>
          }
        >
          <Route index element={<HomeCreateAccount />} />
          <Route path="/Cadastro" element={<Register />} />
          <Route path="/Entrar" element={<SignIn />} />
        </Route>

        <Route path="/Trocar-senha" element={<Reset />}>
          <Route index element={<EnterResetStep />} />
          <Route path="/Trocar-senha/Sucesso" element={<ResetSuccess />} />
        </Route>

        <Route path="/Recuperacao-de-conta" element={<PasswordRecover />} />
        <Route path="/Recuperacao-de-conta/sucesso" element={<RecoverSuccess />} />

        {/* === DASHBOARD PRINCIPAL (PROTEGIDO) === */}
        <Route
          path={dashboardPath}
          element={
            <ProtectedRouteDashboard>
              <Dashboard />
            </ProtectedRouteDashboard>
          }
        >
          <Route index element={<DashboardFavoriteCardHome />} />
          <Route path={dashboardPath + "/Equipamentos"} element={<DashboardEquipment />} />
          <Route path={dashboardPath + "/Pre-Agendamento/Abertura-Devolucao"} element={<PrescheduleDevolutionContainer />} />
          <Route path={dashboardPath + "/Chamados"} element={<TicketHome />}>
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path={dashboardPath + "/Chamados/Abertura-Chamado"} element={<TechnicalTicketStepsContainer />} />
          <Route path={dashboardPath + "/Chamados/Novo-Chamado"} element={<TechnicalTicketOpenNewTicket />} />
          <Route path={dashboardPath + "/Chamados/Abrir-Contestacao"} element={<OpenNewContest />} />
          <Route path={dashboardPath + "/Chamados/Nova-Contestacao"} element={<StepperPage />} />
          <Route path={dashboardPath + "/Chamados/Contestacao/Acompanhar"} element={<ContestTrackPage />} />
          <Route path={dashboardPath + "/Chamados/Acompanhar"} element={<TrackPage />} />
          <Route path={dashboardPath + "/Agendamento/Proposta"} element={<CallingSchedule />} />
          <Route path={dashboardPath + "/Agendamento/Proposta/Segunda-via-nota-de-remessa/:id"} element={<CallingDeiveryCopy />} />
          <Route path={dashboardPath + "/Agendamento/Proposta/Compartilhar-pre-agendamento/:id"} element={<CallingSharePreSchedule />} />
          <Route path={dashboardPath + "/Financeiro"} element={<FinanceHome />} />
          <Route path={dashboardPath + "/Financeiro/Segunda-via/:id"} element={<FinanceDuplicate />} />
        </Route>

        {/* === PORTAL QR (PÚBLICO) === */}
        <Route
          path="/portal-qr"
          element={
            <ProtectedHome>
              <QRPortalHome />
            </ProtectedHome>
          }
        />

        {/* Central de recursos QR (detecta contexto internamente) */}
        <Route
          path="/portal-qr/familia/:familia"
          element={
            <ProtectedHome>
              <QRCentralRecursos />
            </ProtectedHome>
          }
        />

        {/* Viewer unificado QR - detecta contexto e filtra manuais internamente */}
        <Route
          path="/portal-qr/familia/:familia/manual-viewer"
          element={
            <ProtectedHome>
              <QRManualViewer />
            </ProtectedHome>
          }
        />

        {/* Rotas tipadas de manuais QR (operacoes|pecas|servicos) */}
        <Route
          path="/portal-qr/familia/:familia/manual-:tipo(operacoes|pecas|servicos)"
          element={
            <ProtectedHome>
              <QRManualViewer />
            </ProtectedHome>
          }
        />

        {/* Vídeos institucionais QR */}
        <Route
          path="/portal-qr/videos/:familia"
          element={
            <ProtectedHome>
              <QRVideosInstitucionais />
            </ProtectedHome>
          }
        />

        {/* Checklist PEMT - Portal Cliente */}
        <Route
          path="/portal-qr/checklist-pemt/:familia"
          element={
            <ProtectedHome>
              <ChecklistPEMT />
            </ProtectedHome>
          }
        />

        {/* === COMPATIBILIDADE ROTAS ANTIGAS QR === */}
        
        {/* /conteudo/:frota → /familia/:familia */}
        <Route
          path="/portal-qr/conteudo/:frota"
          element={
            <ProtectedHome>
              <RedirectFrotaToFamilia />
            </ProtectedHome>
          }
        />

        {/* /portal-qr/manual/:familia → /portal-qr/familia/:familia/manual-viewer */}
        <Route
          path="/portal-qr/manual/:familia"
          element={
            <ProtectedHome>
              <RedirectOldManualRoutes />
            </ProtectedHome>
          }
        />

        {/* /portal-qr/manual/:familia/:manualId → viewer novo */}
        <Route
          path="/portal-qr/manual/:familia/:manualId"
          element={
            <ProtectedHome>
              <Navigate
                to={({ params, search }) => {
                  const searchParams = new URLSearchParams(search);
                  const modelo = searchParams.get("modelo");
                  const frota = searchParams.get("frota");
                  
                  let newPath = `/portal-qr/familia/${params.familia}/manual-viewer`;
                  const newParams = [];
                  if (modelo) newParams.push(`modelo=${encodeURIComponent(modelo)}`);
                  if (frota) newParams.push(`frota=${encodeURIComponent(frota)}`);
                  if (newParams.length > 0) newPath += `?${newParams.join('&')}`;
                  
                  return newPath;
                }}
                replace
              />
            </ProtectedHome>
          }
        />

        {/* === PORTAL TÉCNICO === */}
        
        {/* Autenticação */}
        <Route
          path="/portal-tecnico/login"
          element={
            <ProtectedHome>
              <PortalTecnicoLogin />
            </ProtectedHome>
          }
        />
        <Route
          path="/portal-tecnico/callback"
          element={
            <ProtectedHome>
              <AzureCallback />
            </ProtectedHome>
          }
        />

        {/* Dashboard/Home Técnico */}
        <Route
          path="/portal-tecnico/dashboard"
          element={
            <ProtectedPortalTecnico>
              <PortalTecnicoHome />
            </ProtectedPortalTecnico>
          }
        />

        {/* Central de recursos Técnico (mesmo componente, detecta contexto) */}
        <Route
          path="/portal-tecnico/familia/:familia"
          element={
            <ProtectedPortalTecnico>
              <QRCentralRecursos />
            </ProtectedPortalTecnico>
          }
        />

        {/* Viewer unificado Técnico - SEM filtros, todos os manuais */}
        <Route
          path="/portal-tecnico/familia/:familia/manual-viewer"
          element={
            <ProtectedPortalTecnico>
              <QRManualViewer />
            </ProtectedPortalTecnico>
          }
        />

        {/* Rotas tipadas de manuais Técnico (operacoes|pecas|servicos) */}
        <Route
          path="/portal-tecnico/familia/:familia/manual-:tipo(operacoes|manutencao|pecas|servicos|tecnico|seguranca)"
          element={
            <ProtectedPortalTecnico>
              <QRManualViewer />
            </ProtectedPortalTecnico>
          }
        />

        {/* Vídeos técnicos (mesmo componente, detecta contexto) */}
        <Route
          path="/portal-tecnico/videos/:familia"
          element={
            <ProtectedPortalTecnico>
              <QRVideosInstitucionais />
            </ProtectedPortalTecnico>
          }
        />

        {/* Analytics e ferramentas técnicas (para futuro) */}
        <Route
          path="/portal-tecnico/analytics/:familia"
          element={
            <ProtectedPortalTecnico>
              <div>Analytics em desenvolvimento</div>
            </ProtectedPortalTecnico>
          }
        />

        <Route
          path="/portal-tecnico/diagnosticos/:familia"
          element={
            <ProtectedPortalTecnico>
              <div>Diagnósticos em desenvolvimento</div>
            </ProtectedPortalTecnico>
          }
        />

        <Route
          path="/portal-tecnico/treinamentos/:familia"
          element={
            <ProtectedPortalTecnico>
              <div>Treinamentos em desenvolvimento</div>
            </ProtectedPortalTecnico>
          }
        />

        <Route
          path="/portal-tecnico/ferramentas/:familia"
          element={
            <ProtectedPortalTecnico>
              <div>Ferramentas em desenvolvimento</div>
            </ProtectedPortalTecnico>
          }
        />

        {/* Checklists Técnicos */}
        <Route
          path="/portal-tecnico/checklist-inspecao/:familia"
          element={
            <ProtectedPortalTecnico>
              <ChecklistInspecao />
            </ProtectedPortalTecnico>
          }
        />

        <Route
          path="/portal-tecnico/checklist-pecas/:familia"
          element={
            <ProtectedPortalTecnico>
              <ChecklistPecas />
            </ProtectedPortalTecnico>
          }
        />

        {/* Redirect /portal-tecnico → /portal-tecnico/login */}
        <Route 
          path="/portal-tecnico" 
          element={<Navigate to="/portal-tecnico/login" replace />} 
        />

        {/* === FALLBACK === */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

export default AnimatedRoutes;